<template>
  <div class="bank-account">
    <div class="panel-page">
      <div class="panel-heading">
        <h2>收款账户</h2>
        <span class="pull-right">
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openAdd">添加收款账户</el-button>
        </span>
      </div>

      <div class="alert alert-info-light">
        提现前需先绑定收款账户，最多可绑定 5 个。请确保账户信息与实名认证信息一致，因信息填写错误导致的打款失败与资金损失由开发者自行承担。
        为保障账号安全，列表中的账号已脱敏展示。
      </div>

      <div v-if="list.length === 0 && !loading" class="empty-box">
        <i class="el-icon-bank-card empty-icon"></i>
        <p>还没有绑定收款账户</p>
        <el-button type="primary" @click="openAdd">添加收款账户</el-button>
      </div>

      <el-row :gutter="12" v-loading="loading">
        <el-col :span="12" v-for="item in list" :key="item.id">
          <div class="bank-card" :class="{ 'is-default': item.isDefault === 1 }">
            <div class="bank-head">
              <span class="bank-type">
                <i :class="typeIcon(item.accountType)"></i>
                {{ typeText(item.accountType) }}
              </span>
              <el-tag v-if="item.isDefault === 1" type="success" size="mini">默认账户</el-tag>
            </div>
            <p class="bank-no">{{ item.accountNoMask }}</p>
            <p class="bank-sub">收款人：{{ item.accountName }}</p>
            <p class="bank-sub" v-if="item.bankName">开户行：{{ item.bankName }}</p>
            <div class="bank-actions">
              <el-button v-if="item.isDefault !== 1" type="text" size="mini" @click="setDefault(item)">设为默认</el-button>
              <el-button type="text" size="mini" @click="openEdit(item)">编辑</el-button>
              <el-button type="text" size="mini" class="del-btn" @click="remove(item)">删除</el-button>
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- 新增 / 编辑 -->
      <el-dialog :title="dialogTitle" :visible.sync="dialogOpen" width="520px" :close-on-click-modal="false">
        <el-form ref="form" :model="form" :rules="rules" label-width="100px">
          <el-form-item label="账户类型" prop="accountType">
            <el-radio-group v-model="form.accountType">
              <el-radio :label="1">支付宝</el-radio>
              <el-radio :label="2">微信</el-radio>
              <el-radio :label="3">银行卡</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="收款人姓名" prop="accountName">
            <el-input v-model="form.accountName" placeholder="须与实名认证的姓名一致" />
          </el-form-item>
          <el-form-item :label="accountNoLabel" prop="accountNo">
            <el-input v-model="form.accountNo" :placeholder="accountNoPlaceholder" />
          </el-form-item>
          <el-form-item v-if="form.accountType === 3" label="开户行" prop="bankName">
            <el-input v-model="form.bankName" placeholder="如：中国工商银行深圳科技园支行" />
          </el-form-item>
          <el-form-item label="设为默认">
            <el-switch v-model="form.isDefault" :active-value="1" :inactive-value="0" />
          </el-form-item>
          <el-form-item v-if="isEdit">
            <p class="edit-tip">出于安全考虑，编辑时需重新填写完整账号信息。</p>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button type="primary" :loading="submitting" @click="submit">确 定</el-button>
          <el-button @click="dialogOpen = false">取 消</el-button>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import {
  listBankAccounts,
  addBankAccount,
  editBankAccount,
  setDefaultBank,
  deleteBankAccount
} from '@/api/developer'

const emptyForm = () => ({
  id: undefined,
  accountType: 1,
  accountName: '',
  accountNo: '',
  bankName: '',
  isDefault: 0
})

export default {
  name: 'bankAccount',
  data () {
    return {
      loading: true,
      submitting: false,
      list: [],
      dialogOpen: false,
      form: emptyForm(),
      rules: {
        accountType: [{ required: true, message: '请选择账户类型', trigger: 'change' }],
        accountName: [{ required: true, message: '收款人姓名不能为空', trigger: 'blur' }],
        accountNo: [{ required: true, message: '收款账号不能为空', trigger: 'blur' }],
        bankName: [{ required: true, message: '银行卡必须填写开户行', trigger: 'blur' }]
      }
    }
  },
  computed: {
    isEdit () {
      return !!this.form.id
    },
    dialogTitle () {
      return this.isEdit ? '编辑收款账户' : '添加收款账户'
    },
    accountNoLabel () {
      if (this.form.accountType === 1) return '支付宝账号'
      if (this.form.accountType === 2) return '微信号'
      return '银行卡号'
    },
    accountNoPlaceholder () {
      if (this.form.accountType === 1) return '请输入支付宝账号（手机号或邮箱）'
      if (this.form.accountType === 2) return '请输入微信号'
      return '请输入银行卡号'
    }
  },
  methods: {
    typeText (type) {
      const map = { 1: '支付宝', 2: '微信', 3: '银行卡' }
      return map[Number(type)] || '其他'
    },
    typeIcon (type) {
      const map = { 1: 'el-icon-money', 2: 'el-icon-chat-dot-round', 3: 'el-icon-bank-card' }
      return map[Number(type)] || 'el-icon-wallet'
    },
    loadList () {
      this.loading = true
      listBankAccounts().then(res => {
        const { code, data, msg } = res
        if (code === 0) {
          this.list = Array.isArray(data) ? data : []
        } else {
          this.$message.error(msg || '获取收款账户失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    openAdd () {
      this.form = emptyForm()
      this.dialogOpen = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    openEdit (item) {
      this.form = {
        id: item.id,
        accountType: Number(item.accountType),
        accountName: item.accountName,
        // 列表返回的是脱敏账号，需用户重新填写
        accountNo: '',
        bankName: item.bankName || '',
        isDefault: Number(item.isDefault) || 0
      }
      this.dialogOpen = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    submit () {
      this.$refs.form.validate(valid => {
        if (!valid) return
        this.submitting = true
        const request = this.isEdit ? editBankAccount : addBankAccount
        request(this.form).then(res => {
          const { code, msg } = res
          if (code === 0) {
            this.$message.success(msg || '操作成功')
            this.dialogOpen = false
            this.loadList()
          } else {
            this.$message.error(msg || '操作失败')
          }
        }).finally(() => {
          this.submitting = false
        })
      })
    },
    setDefault (item) {
      setDefaultBank(item.id).then(res => {
        const { code, msg } = res
        if (code === 0) {
          this.$message.success(msg || '设置成功')
          this.loadList()
        } else {
          this.$message.error(msg || '设置失败')
        }
      })
    },
    remove (item) {
      this.$confirm(`确认删除该${this.typeText(item.accountType)}账户（${item.accountNoMask}）？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        deleteBankAccount(item.id).then(res => {
          const { code, msg } = res
          if (code === 0) {
            this.$message.success(msg || '删除成功')
            this.loadList()
          } else {
            this.$message.error(msg || '删除失败')
          }
        })
      }).catch(() => {})
    }
  },
  created () {
    this.loadList()
  }
}
</script>

<style lang="less" scoped>
.bank-account {
  .panel-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 22px;
      color: #303133;
    }
  }

  .alert-info-light {
    background-color: #d9edf7;
    border-color: #c6e4f3;
    color: #3a87ad;
    padding: 15px;
    margin-bottom: 20px;
    border: 1px solid transparent;
    border-radius: 3px;
    font-size: 13px;
    line-height: 22px;
  }

  .bank-card {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 16px 18px;
    margin-bottom: 14px;
    transition: all 0.2s;

    &:hover {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    }

    &.is-default {
      border-color: #0d95e8;
      background: #f7fcff;
    }

    .bank-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;

      .bank-type {
        font-weight: 600;
        color: #303133;

        i {
          margin-right: 6px;
          color: #0d95e8;
        }
      }
    }

    .bank-no {
      margin: 0 0 6px 0;
      font-size: 18px;
      letter-spacing: 1px;
      color: #303133;
      font-family: 'SF Mono', Consolas, Menlo, monospace;
    }

    .bank-sub {
      margin: 0;
      font-size: 13px;
      color: #909399;
      line-height: 20px;
    }

    .bank-actions {
      margin-top: 10px;
      text-align: right;

      .del-btn {
        color: #f56c6c;
      }
    }
  }

  .empty-box {
    padding: 50px 0;
    text-align: center;
    color: #909399;

    .empty-icon {
      font-size: 48px;
      color: #c0c4cc;
    }

    p {
      margin: 14px 0 18px 0;
    }
  }

  .edit-tip {
    margin: 0;
    font-size: 12px;
    color: #e6a23c;
  }
}
</style>
